import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const userData = await auth.me();
      setUser(userData);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const userData = await auth.login(email, password);
    if (userData?.requires_2fa) {
      setUser(false);
      return userData;
    }
    setUser(userData);
    return userData;
  };

  const verify2fa = async (challengeToken, code) => {
    const userData = await auth.verify2fa(challengeToken, code);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const userData = await auth.register(email, password, name);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    await auth.logout();
    setUser(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isImpersonating: !!user?.impersonated_by,
    impersonatedBy: user?.impersonated_by_email || null,
    login,
    verify2fa,
    register,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
